/**
 * Turning a finished draft into a file payload — pure, so every surface (download,
 * clipboard, share sheet, API response) receives exactly the same bytes and names.
 */

import {
  type EventDraft,
  type EventFormatId,
  type EventProblem,
  type FormatContext,
  eventFileName,
  eventFormat,
  renderEvent,
  sanitizeDraft,
  validateEvent,
  toIcsCalendar,
} from '@project/event-format';

export interface EventExport {
  readonly formatId: EventFormatId;
  readonly fileName: string;
  readonly mimeType: string;
  readonly content: string;
}

export type EventExportResult =
  | { readonly ok: true; readonly export: EventExport }
  | { readonly ok: false; readonly problems: readonly EventProblem[] };

/** Renders a draft, refusing anything validation rejects — the gate, not a hint. */
export function exportDraft(
  draft: EventDraft,
  formatId: EventFormatId,
  context: FormatContext,
): EventExportResult {
  // Validate what the caller actually holds (so the messages match the form), then render
  // from a sanitized copy — the draft may have arrived from a host, an API, or a model.
  const problems = validateEvent(draft);
  if (problems.length > 0) return { ok: false, problems };
  const safe = sanitizeDraft(draft);
  return {
    ok: true,
    export: {
      formatId,
      fileName: eventFileName(formatId, safe),
      mimeType: eventFormat(formatId).mimeType,
      content: renderEvent(formatId, safe, context),
    },
  };
}

/** A `data:` URL for the export — the delivery every host can use, embed included. */
export function exportDataUrl(payload: Pick<EventExport, 'mimeType' | 'content'>): string {
  return `data:${payload.mimeType},${encodeURIComponent(payload.content)}`;
}

export function exportCalendar(
  drafts: readonly EventDraft[],
  context: FormatContext,
): EventExportResult {
  const problems = drafts.flatMap((draft) => validateEvent(draft));
  if (problems.length > 0) return { ok: false, problems };
  const safe = drafts.map((draft, index) => ({
    ...sanitizeDraft(draft),
    uid: draft.uid ?? `${context.uid}-${index + 1}`,
  }));
  return {
    ok: true,
    export: {
      formatId: 'ics',
      fileName: 'events.ics',
      mimeType: 'text/calendar',
      content: toIcsCalendar(safe, context),
    },
  };
}
